import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import {Link} from 'react-router-dom'
import {
  FaHistory,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaInbox,
  FaClipboardList,
  FaClock,
  FaBan,
  FaEdit,
  FaCalendar,
  FaClipboardList as FaClipboardListIcon
} from 'react-icons/fa';
import { RiEditLine } from 'react-icons/ri';
import API_BASE_URL from '../../../../config/api';
import Topbar from '../../mini-comp/Topbar';
import Sidebar from '../../mini-comp/Sidebar';

const ManagerPayrollLeaveHistory = () => {
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: '',
    expected_delivery_date: ''
  });
  const [maternityDates, setMaternityDates] = useState({
    earliest: null,
    latest: null
  });

  const showAlert = (type, message) => {
    Swal.fire({
      icon: type,
      title: type === 'success' ? 'Success!' : 'Error!',
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
    });
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  };

  const calculateMaternityLeaveDates = (deliveryDate) => {
    if (!deliveryDate) return null;
    const expectedDate = new Date(deliveryDate);
    const earliestDate = new Date(expectedDate);
    earliestDate.setDate(expectedDate.getDate() - 45);
    const latestDate = new Date(expectedDate);
    latestDate.setDate(expectedDate.getDate() - 21);
    return {
      earliest: earliestDate.toISOString().split('T')[0],
      latest: latestDate.toISOString().split('T')[0],
      deliveryDate: expectedDate.toISOString().split('T')[0]
    };
  };

  const calculateEndDate = (startDate) => {
    if (!startDate) return null;
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 97);
    return end.toISOString().split('T')[0];
  };

  const validateMaternityDates = () => {
    if (editFormData.leave_type === 'maternity' && editFormData.expected_delivery_date) {
      const deliveryDate = new Date(editFormData.expected_delivery_date);
      const startDate = new Date(editFormData.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (deliveryDate < today) {
        showAlert('error', 'Expected delivery date cannot be in the past');
        return false;
      }

      const earliestDate = new Date(maternityDates.earliest);
      const latestDate = new Date(maternityDates.latest);

      if (startDate < earliestDate || startDate > latestDate) {
        showAlert('error', 'Start date must be between 45 and 21 days before expected delivery date');
        return false;
      }

      const calculatedEndDate = calculateEndDate(editFormData.start_date);
      if (editFormData.end_date !== calculatedEndDate) {
        showAlert('error', 'End date must be exactly 98 days from start date');
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    fetchLeaveHistory();
  }, []);

  const fetchLeaveHistory = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/payroll/my-leaves/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLeaveHistory(response.data);
    } catch (error) {
      console.error('Error fetching leave history:', error);
      // Don't show error toast on fetch fail to avoid noise, unless it's critical
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    const initialData = {
      leave_type: leave.leave_type || '',
      start_date: leave.start_date || '',
      end_date: leave.end_date || '',
      reason: leave.reason || '',
      expected_delivery_date: leave.expected_delivery_date || ''
    };
    setEditFormData(initialData);

    if (leave.leave_type === 'maternity' && leave.expected_delivery_date) {
      const dates = calculateMaternityLeaveDates(leave.expected_delivery_date);
      setMaternityDates(dates);
    }

    setIsEditModalOpen(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'leave_type') {
      if (value === 'maternity') {
        setEditFormData(prev => ({
          ...prev,
          leave_type: value,
          expected_delivery_date: '',
          start_date: '',
          end_date: ''
        }));
      } else {
        setEditFormData(prev => ({
          ...prev,
          leave_type: value
        }));
      }
    } else if (name === 'expected_delivery_date') {
      const dates = calculateMaternityLeaveDates(value);
      if (dates) {
        setMaternityDates(dates);
        setEditFormData(prev => ({
          ...prev,
          expected_delivery_date: value,
          start_date: '',
          end_date: ''
        }));
      }
    } else if (name === 'start_date' && editFormData.leave_type === 'maternity') {
      const endDate = calculateEndDate(value);
      setEditFormData(prev => ({
        ...prev,
        start_date: value,
        end_date: endDate || ''
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleUpdateLeave = async (e) => {
    e.preventDefault();

    // Validation logic from PayrollApplyLeave
    if (editFormData.leave_type !== 'maternity') {
      const startDate = new Date(editFormData.start_date);
      const endDate = new Date(editFormData.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        showAlert('error', 'Start date cannot be in the past');
        return;
      }

      if (endDate < startDate) {
        showAlert('error', 'End date must be after start date');
        return;
      }
    } else {
      if (!validateMaternityDates()) {
        return;
      }
    }

    try {
      const token = Cookies.get('token');
      await axios.patch(`${API_BASE_URL}/api/v1/payroll/leaves/${selectedLeave.id}/`, editFormData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Leave request updated successfully.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });

      setIsEditModalOpen(false);
      fetchLeaveHistory();
    } catch (error) {
      console.error('Error updating leave:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update leave request'
      });
    }
  };

  const handleCancelLeave = async (leave, e) => {
    if (e) e.stopPropagation();

    try {
      const result = await Swal.fire({
        title: 'Cancel Leave Request',
        text: 'Are you sure you want to cancel this leave request?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'No, keep it'
      });

      if (result.isConfirmed) {
        const token = Cookies.get('token');
        await axios.patch(`${API_BASE_URL}/api/v1/payroll/leaves/${leave.id}/`, {
          status: 'cancelled'
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Update the leave status in the local state
        setLeaveHistory(prevHistory =>
          prevHistory.map(item =>
            item.id === leave.id
              ? { ...item, status: 'cancelled' }
              : item
          )
        );

        Swal.fire({
          icon: 'success',
          title: 'Cancelled!',
          text: 'Your leave request has been cancelled.',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }
    } catch (error) {
      console.error('Error cancelling leave:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to cancel leave request'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <span className="badge bg-success-transparent text-success fs-12 fw-bold px-3 py-2 rounded-pill"><FaCheck className="me-1" /> Approved</span>;
      case 'rejected':
        return <span className="badge bg-danger-transparent text-danger fs-12 fw-bold px-3 py-2 rounded-pill"><FaTimes className="me-1" /> Rejected</span>;
      case 'cancelled':
        return <span className="badge bg-secondary-transparent text-secondary fs-12 fw-bold px-3 py-2 rounded-pill"><FaBan className="me-1" /> Cancelled</span>;
      default:
        return <span className="badge bg-warning-transparent text-warning fs-12 fw-bold px-3 py-2 rounded-pill"><FaClock className="me-1" /> Pending</span>;
    }
  };

  return (
    <div>
      <Topbar />
      <Sidebar />

      <div className="app-content-area">
        <div className="app-content-wrap">
          <div className="container-fluid">
            <div className="row">

              <div className="col-xl-12">
                
                <div className="card">
                   <div className="card-header justify-between">
                    <h4 className="d-flex-items gap-10">Leave History<span className="badge bg-label-primary">{leaveHistory.length}</span></h4>
                    <div className="d-flex flex-wrap gap-15">
                      <Link className="btn btn-primary text-white" to="/manager/payroll/apply-leave">
                        Apply Leave
                      </Link>

                    </div>
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="text-center py-5">
                        <FaSpinner className="fa-spin fs-40 text-primary mb-3" />
                        <p className="fs-16 text-muted">Loading leave history...</p>
                      </div>
                    ) : leaveHistory.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="avatar avatar-xxl bg-light text-muted mb-4 mx-auto">
                          <FaInbox size={40} />
                        </div>
                        <h4 className="fw-bold">No Leave History Found</h4>
                        <p className="text-muted">You haven't made any leave requests yet.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="bg-light-subtle">
                            <tr>
                              <th className="ps-4">Leave Type</th>
                              <th>Duration</th>
                              <th>Period</th>
                              <th>Reason</th>
                              <th>Status</th>
                              <th className="text-end pe-4">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveHistory.map((leave) => (
                              <tr key={leave.id}>
                                <td className="ps-4">
                                  <div className="d-flex align-items-center gap-10">
                                    <div className="avatar avatar-md bg-primary-transparent text-primary rounded-circle">
                                      <FaClipboardList />
                                    </div>
                                    <div>
                                      <h6 className="mb-0 fw-bold text-capitalize">{leave.leave_type}</h6>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="fw-medium text-dark">
                                    {leave.duration} {leave.leave_type?.toLowerCase() === 'maternity' || leave.leave_type?.toLowerCase() === 'sick' ? 'Days' : 'Working Days'}
                                  </span>
                                </td>
                                <td>
                                  <div className="fs-13 text-muted">
                                    <FaCalendarAlt className="me-1 text-primary-light" />
                                    {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '200px' }} title={leave.reason}>
                                    {leave.reason || <span className="text-muted italic small">No reason provided</span>}
                                  </div>
                                </td>
                                <td>{getStatusBadge(leave.status)}</td>
                                <td className="text-end pe-4">
                                  {leave.status?.toLowerCase() === 'pending' && (
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-5"
                                        onClick={() => handleEditClick(leave)}
                                      >
                                        <RiEditLine size={12} /> Edit
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-5"
                                        onClick={(e) => handleCancelLeave(leave, e)}
                                      >
                                        <FaBan size={12} /> Cancel
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Leave Modal */}
      {isEditModalOpen && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Leave Request</h5>
                <button type="button" className="btn-close" onClick={() => setIsEditModalOpen(false)}></button>
              </div>
              <form onSubmit={handleUpdateLeave}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Leave Type</label>
                    <select
                      className="form-select"
                      name="leave_type"
                      value={editFormData.leave_type}
                      onChange={handleEditFormChange}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="vacation">Vacation</option>
                      <option value="special">Special</option>
                      <option value="sick">Sick</option>
                      <option value="maternity">Maternity</option>
                    </select>
                  </div>

                  {editFormData.leave_type === 'maternity' && (
                    <div className="col-12 mb-3">
                      <div className="alert alert-info border-0 shadow-sm d-flex gap-15 align-items-start small p-2">
                        <FaCalendar className="mt-1 flex-shrink-0" />
                        <div>
                          <h6 className="fw-bold mb-1 fs-12">Maternity Leave Guidelines</h6>
                          <p className="mb-0 fs-11">
                            Maternity leave is strictly 98 days. Start date must be between 45 and 21 days before delivery.
                          </p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Expected Date of Delivery</label>
                        <input
                          type="date"
                          className="form-control"
                          name="expected_delivery_date"
                          value={editFormData.expected_delivery_date}
                          onChange={handleEditFormChange}
                          required
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>

                      {editFormData.expected_delivery_date && maternityDates.earliest && (
                        <div className="p-2 bg-light-subtle rounded mb-3 border fs-12 text-muted">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="fw-bold">Earliest Start:</span>
                            <span>{formatDateForDisplay(maternityDates.earliest)}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="fw-bold">Latest Start:</span>
                            <span>{formatDateForDisplay(maternityDates.latest)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="start_date"
                        value={editFormData.start_date}
                        onChange={handleEditFormChange}
                        required
                        min={editFormData.leave_type === 'maternity' ? maternityDates.earliest : new Date().toISOString().split('T')[0]}
                        max={editFormData.leave_type === 'maternity' ? maternityDates.latest : undefined}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="end_date"
                        value={editFormData.end_date}
                        onChange={handleEditFormChange}
                        required
                        readOnly={editFormData.leave_type === 'maternity'}
                        min={editFormData.start_date || new Date().toISOString().split('T')[0]}
                      />
                      {editFormData.leave_type === 'maternity' && <small className="text-muted d-block mt-1 fs-11">Calculated (98 days)</small>}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Reason</label>
                    <textarea
                      className="form-control"
                      name="reason"
                      rows="3"
                      value={editFormData.reason}
                      onChange={handleEditFormChange}
                      placeholder={editFormData.leave_type === 'maternity' ? "Additional details..." : "Why are you requesting leave?"}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerPayrollLeaveHistory;
